/**
 * XpGainToast - Global floating toast for XP gain notifications
 *
 * Renders when XP is earned via chat, SRS, or scenarios.
 * Supports three variants:
 * - regular: "+X XP" with activity icon
 * - level-up: "Level Up!" with tier color
 * - milestone: "Milestone achieved!" with distinct styling
 *
 * Uses AnimatePresence for enter/exit animations.
 * Stacks up to 3 toasts.
 */

import React from 'react';
import { AnimatePresence, MotionDiv, useReducedMotion } from '@/components/ui/motion';
import { useXpToast } from '@/hooks/useXpToast';
import type { RecentXpGain } from '@/stores/xpStore';
import {
  MessageSquare,
  CheckCircle,
  XCircle,
  Award,
  Sparkles,
  Target,
  Trophy,
  Sun,
  Star,
  X,
  Zap,
  BookOpen,
  Languages,
  Layers,
} from '@/utils/icons';
import type { XpEventType } from '@/types/xp';
import { cn } from '@/lib/utils';

// Map event types to icon components
const EVENT_ICON_MAP: Record<XpEventType, React.ComponentType<{ className?: string }>> = {
  chat_message: MessageSquare,
  srs_review_correct: CheckCircle,
  srs_review_incorrect: XCircle,
  srs_card_mastered: Award,
  new_word_discovered: Sparkles,
  scenario_objective: Target,
  scenario_completed: Trophy,
  scenario_phase_completed: Layers,
  daily_bonus: Sun,
  grammar_card_correct: BookOpen,
  grammar_card_incorrect: BookOpen,
  sentence_card_correct: Languages,
  sentence_card_incorrect: Languages,
  sentence_card_created: Languages,
};

function XpToastItem({
  toast,
  onDismiss,
}: {
  toast: RecentXpGain;
  onDismiss: () => void;
}) {
  const prefersReduced = useReducedMotion();
  const Icon = EVENT_ICON_MAP[toast.eventType] ?? Zap;

  if (toast.variant === 'level-up') {
    return (
      <MotionDiv
        layout
        initial={prefersReduced ? { opacity: 0 } : { opacity: 0, x: 80, scale: 0.95 }}
        animate={prefersReduced ? { opacity: 1 } : { opacity: 1, x: 0, scale: 1 }}
        exit={prefersReduced ? { opacity: 0 } : { opacity: 0, x: 80, scale: 0.95 }}
        transition={{ type: 'spring', stiffness: 400, damping: 28 }}
        className={cn(
          'pointer-events-auto relative flex items-center gap-3 rounded-xl px-4 py-3',
          'bg-background border-2 shadow-lg shadow-primary/10',
          'min-w-[240px] max-w-[320px]'
        )}
        style={{ borderColor: toast.tierColor ?? 'var(--primary)' }}
        role="status"
        aria-live="polite"
      >
        <div
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-white font-bold text-sm"
          style={{ backgroundColor: toast.tierColor ?? 'var(--primary)' }}
        >
          <Star className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold" style={{ color: toast.tierColor }}>
            Level Up!
          </div>
          <div className="text-xs text-muted-foreground truncate">
            Level {toast.newLevel} &middot; {toast.tierName}
          </div>
        </div>
        <div className="text-sm font-bold text-foreground">+{toast.amount}</div>
        <button
          onClick={onDismiss}
          className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-muted hover:bg-muted-foreground/20 transition-colors"
          aria-label="Dismiss"
        >
          <X className="h-3 w-3 text-muted-foreground" />
        </button>
      </MotionDiv>
    );
  }

  if (toast.variant === 'milestone') {
    return (
      <MotionDiv
        layout
        initial={prefersReduced ? { opacity: 0 } : { opacity: 0, x: 80, scale: 0.95 }}
        animate={prefersReduced ? { opacity: 1 } : { opacity: 1, x: 0, scale: 1 }}
        exit={prefersReduced ? { opacity: 0 } : { opacity: 0, x: 80, scale: 0.95 }}
        transition={{ type: 'spring', stiffness: 400, damping: 28 }}
        className={cn(
          'pointer-events-auto relative flex items-center gap-3 rounded-xl px-4 py-3',
          'bg-background border-2 border-amber-400 shadow-lg shadow-amber-500/10',
          'min-w-[240px] max-w-[320px]'
        )}
        role="status"
        aria-live="polite"
      >
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-500/10">
          <Trophy className="h-5 w-5 text-amber-500" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-amber-600 dark:text-amber-400">
            Milestone!
          </div>
          <div className="text-xs text-muted-foreground truncate">
            {toast.milestoneTitle}
          </div>
        </div>
        <button
          onClick={onDismiss}
          className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-muted hover:bg-muted-foreground/20 transition-colors"
          aria-label="Dismiss"
        >
          <X className="h-3 w-3 text-muted-foreground" />
        </button>
      </MotionDiv>
    );
  }

  // Regular XP toast
  return (
    <MotionDiv
      layout
      initial={prefersReduced ? { opacity: 0 } : { opacity: 0, x: 80, scale: 0.95 }}
      animate={prefersReduced ? { opacity: 1 } : { opacity: 1, x: 0, scale: 1 }}
      exit={prefersReduced ? { opacity: 0 } : { opacity: 0, x: 80, scale: 0.95 }}
      transition={{ type: 'spring', stiffness: 400, damping: 28 }}
      className={cn(
        'pointer-events-auto relative flex items-center gap-3 rounded-xl px-4 py-2.5',
        'bg-background border shadow-md',
        'min-w-[200px] max-w-[280px]'
      )}
      role="status"
      aria-live="polite"
    >
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10">
        <Icon className="h-4 w-4 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs text-muted-foreground truncate">{toast.reason}</div>
      </div>
      {toast.amount > 0 && (
        <div className="text-sm font-bold text-primary">+{toast.amount}</div>
      )}
      <button
        onClick={onDismiss}
        className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-muted hover:bg-muted-foreground/20 transition-colors"
        aria-label="Dismiss"
      >
        <X className="h-3 w-3 text-muted-foreground" />
      </button>
    </MotionDiv>
  );
}

export function XpGainToast() {
  const { toasts, dismiss } = useXpToast();

  return (
    <div
      className="fixed top-4 right-4 z-[100] flex flex-col gap-2 pointer-events-none"
      aria-label="XP notifications"
    >
      <AnimatePresence mode="popLayout">
        {toasts.map((toast) => (
          <XpToastItem
            key={toast.id}
            toast={toast}
            onDismiss={() => dismiss(toast.id)}
          />
        ))}
      </AnimatePresence>
    </div>
  );
}
